import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import { useToast } from "../../../../hooks/useToast";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string | null;
  content: string;
  subject: string | null;
  is_read: boolean;
  created_at: string;
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/client", icon: "ri-dashboard-line" },
  { label: "Appointments", path: "/dashboard/client/appointments", icon: "ri-calendar-event-line" },
  { label: "Messages", path: "/dashboard/client/messages", icon: "ri-chat-3-line" },
  { label: "Settings", path: "/dashboard/client/settings", icon: "ri-settings-3-line" },
];

export default function ClientMessages() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState("");
  const [activeTab, setActiveTab] = useState<"inbox" | "sent">("inbox");

  useEffect(() => {
    if (!user) return;
    fetchMessages();

    // Real-time subscription for messages
    const channel = supabase
      .channel('messages-client')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
      }, (payload) => {
        fetchMessages();
        if (payload.eventType === 'INSERT' && payload.new?.recipient_id === user?.id) {
          showToast('You have a new message', 'info');
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchMessages = async () => {
    const { data } = await supabase
      .from("messages")
      .select("*")
      .or(`sender_id.eq.${user!.id},recipient_id.eq.${user!.id}`)
      .order("created_at", { ascending: false });
    setMessages(data || []);
    setLoading(false);
  };

  const inboxMessages = messages.filter((m) => m.recipient_id === user?.id);
  const sentMessages = messages.filter((m) => m.sender_id === user?.id);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormMsg("");
    const fd = new FormData(e.currentTarget);
    const subject = fd.get("subject") as string;
    const content = fd.get("content") as string;

    const { error } = await supabase.from("messages").insert({
      sender_id: user!.id,
      recipient_id: null,
      subject,
      content,
    });

    if (error) {
      setFormMsg("Failed to send message.");
    } else {
      setFormMsg("Message sent successfully!");
      setTimeout(() => {
        setShowNewModal(false);
        setFormMsg("");
        fetchMessages();
      }, 1200);
    }
    setSubmitting(false);
  };

  const displayMessages = activeTab === "inbox" ? inboxMessages : sentMessages;

  return (
    <DashboardLayout navItems={navItems} title="Client Portal" subtitle="Messages">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading messages...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2 p-1 bg-white border border-gray-200 rounded-full">
              <button
                onClick={() => setActiveTab("inbox")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeTab === "inbox" ? "bg-brand-green-600 text-white" : "text-gray-600 hover:text-gray-900"}`}
              >
                Inbox ({inboxMessages.length})
              </button>
              <button
                onClick={() => setActiveTab("sent")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeTab === "sent" ? "bg-brand-green-600 text-white" : "text-gray-600 hover:text-gray-900"}`}
              >
                Sent ({sentMessages.length})
              </button>
            </div>
            <button
              onClick={() => setShowNewModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </div>
              New Message
            </button>
          </div>

          {/* Messages List */}
          {displayMessages.length > 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="divide-y divide-gray-100">
                {displayMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 md:p-5 ${activeTab === "inbox" && !msg.is_read ? "bg-brand-green-50/50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {activeTab === "inbox" && !msg.is_read && (
                            <span className="w-2 h-2 rounded-full bg-brand-green-600 shrink-0" />
                          )}
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {msg.subject || "(No subject)"}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2">{msg.content}</p>
                        <p className="text-xs text-gray-400">{formatDate(msg.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 border border-gray-100 text-center">
              <div className="w-14 h-14 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-chat-3-line text-xl text-gray-400" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                {activeTab === "inbox" ? "No messages in your inbox yet." : "No sent messages yet."}
              </p>
              <button
                onClick={() => setShowNewModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2 bg-brand-green-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-green-700 transition-all whitespace-nowrap"
              >
                Send a Message
              </button>
            </div>
          )}
        </div>
      )}

      {/* New Message Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowNewModal(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New Message</h3>
            {formMsg && (
              <div className={`rounded-lg p-3 border mb-4 ${formMsg.includes("success") ? "bg-brand-green-50 border-brand-green-100 text-brand-green-800" : "bg-red-50 border-red-100 text-red-700"}`}>
                <p className="text-sm text-center">{formMsg}</p>
              </div>
            )}
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Subject</label>
                <input
                  type="text"
                  name="subject"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
                  placeholder="What is this about?"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Message</label>
                <textarea
                  name="content"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none resize-none"
                  placeholder="Type your message here..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-soft-gray rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:bg-brand-green-400 transition-all whitespace-nowrap"
                >
                  {submitting ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}